import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Crown, Loader2, Copy, Clock } from "lucide-react";

// Página de checkout dedicada ao app mobile: sem o menu/layout do site,
// pensada pra tela de celular. O usuário chega aqui pelo botão "Assinar no
// site" do app (navegador externo), autenticado via um código de uso único
// (ver /auth/mobile-handoff no backend) — não precisa logar de novo.
const ICONS = { free: Sparkles, pro: Zap, agency: Crown };

export default function MobileCheckoutPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [plan, setPlan] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [busy, setBusy] = useState(false);

  const planKey = searchParams.get("plan");
  const cycle = searchParams.get("cycle") === "yearly" ? "yearly" : "monthly";
  const code = searchParams.get("code");

  useEffect(() => {
    const init = async () => {
      if (!code) {
        setErrorMsg("Link inválido ou expirado. Volte ao app e tente novamente.");
        setStatus("error");
        return;
      }
      try {
        const { data } = await api.post("/auth/mobile-handoff/redeem", { code });
        localStorage.setItem("autopost_token", data.access_token);
        localStorage.setItem("autopost_user", JSON.stringify(data.user));

        const plansRes = await api.get("/billing/plans");
        const found =
          plansRes.data.plans.find((p) => p.key === planKey) ||
          plansRes.data.plans.find((p) => p.key !== "free");
        setPlan(found);
        setStatus("ready");
      } catch (err) {
        setErrorMsg(getErrorMessage(err, "Link inválido ou expirado. Volte ao app e tente novamente."));
        setStatus("error");
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling de confirmação enquanto o QR Code está sendo exibido
  useEffect(() => {
    if (!pixData || pixData.paid) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/billing/status/${pixData.session_id}`);
        if (data.status === "complete") {
          clearInterval(interval);
          setPixData((p) => ({ ...p, paid: true }));
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [pixData]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_key: plan.key,
        cycle,
        origin_url: window.location.origin,
      });
      setPixData({ ...data, paid: false });
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao gerar Pix"));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(pixData.qr_code);
    toast.success("Código Pix copiado!");
  };

  const formatBRL = (n) => `R$ ${Number(n).toFixed(2).replace(".", ",")}`;

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (status === "error" || !plan) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 p-6 text-center">
        <p className="text-zinc-700 mb-4">{errorMsg || "Plano não encontrado."}</p>
        <Button onClick={() => (window.location.href = "/login")} className="rounded-sm">
          Entrar no site
        </Button>
      </div>
    );
  }

  const Icon = ICONS[plan.key] || Sparkles;
  const price = cycle === "yearly" ? plan.yearly_price / 12 : plan.monthly_price;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="h-12 w-12 bg-blue-50 text-blue-600 rounded-sm flex items-center justify-center mx-auto mb-3">
            <Icon className="h-6 w-6" strokeWidth={2} />
          </div>
          <h1 className="font-display text-2xl font-bold">{plan.name}</h1>
          <p className="text-zinc-500 text-sm mt-1">{plan.tagline}</p>
        </div>

        <div className="bg-white border-2 border-blue-600 rounded-sm p-6 shadow-lg shadow-blue-100">
          <div className="text-center mb-6">
            <span className="font-display text-4xl font-bold tracking-tight">{formatBRL(price)}</span>
            <span className="text-zinc-500 text-sm">/mês</span>
            {cycle === "yearly" && (
              <p className="text-xs text-zinc-500 mt-1">Cobrado {formatBRL(plan.yearly_price)} por ano</p>
            )}
          </div>

          {!pixData ? (
            <Button
              onClick={pay}
              disabled={busy}
              className="w-full rounded-sm h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Pagar com Pix
            </Button>
          ) : pixData.paid ? (
            <div className="text-center py-4">
              <Check className="h-10 w-10 text-green-600 mx-auto mb-2" />
              <p className="font-semibold text-green-600">Pagamento confirmado! 🎉</p>
              <p className="text-sm text-zinc-500 mt-1">Pode voltar pro app.</p>
            </div>
          ) : (
            <div>
              {pixData.qr_code_base64 && (
                <div className="flex justify-center mb-4">
                  <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code Pix"
                    className="w-48 h-48 border border-zinc-200 rounded-sm"
                  />
                </div>
              )}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-xs font-mono text-zinc-600 truncate">
                  {pixData.qr_code}
                </div>
                <button onClick={copy} className="flex-shrink-0 px-3 py-2 bg-blue-600 text-white rounded-sm">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                <Clock className="h-4 w-4" />
                Abra o app do seu banco e escaneie ou copie o código
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-zinc-400 text-center mt-6">
          Pagamento 100% seguro via Pix. O acesso é liberado automaticamente.
        </p>
      </div>
    </div>
  );
}
